import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";
import { MockSubscriptionCheckoutPanel } from "@/components/subscription-plans-panel";

type PollState =
  | "confirming"
  | "ready"
  | "timeout"
  | "no-payment"
  /** PAY-5: not signed in (or the session could not be renewed) — sign in and come back. */
  | "sign-in"
  | "not-found"
  | "not-completed"
  | "duplicate"
  | "refunded";

/** Payment states the poll stops on (PAY-5 / PAY-11), and what the page says for each. */
const FINAL_PAYMENT_STATES: Record<string, PollState> = {
  failed: "not-completed",
  expired: "not-completed",
  duplicate: "duplicate",
  refunded: "refunded",
};

function CheckoutSuccessClient({
  tenantSlug,
  paymentId,
  subscriptionPaymentId,
}: {
  tenantSlug: string;
  paymentId?: string;
  subscriptionPaymentId?: string;
}) {
  const t = useTranslations("checkout");
  const [pollState, setPollState] = useState<PollState>(() => {
    if (subscriptionPaymentId) return "ready";
    return paymentId ? "confirming" : "no-payment";
  });

  useEffect(() => {
    if (!paymentId) return;

    let cancelled = false;

    async function pollEnrollment(activePaymentId: string) {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        let res: Response | null = null;
        try {
          res = await apiFetch(
            `/v1/checkout/status?paymentId=${encodeURIComponent(activePaymentId)}`,
          );
        } catch {
          res = null; // network blip: keep polling
        }

        if (cancelled) return;

        if (res?.ok) {
          const json = (await res.json()) as {
            data?: { enrolled?: boolean; paymentStatus?: string };
          };
          if (json.data?.enrolled) {
            setPollState("ready");
            return;
          }
          const final = json.data?.paymentStatus
            ? FINAL_PAYMENT_STATES[json.data.paymentStatus]
            : undefined;
          if (final) {
            setPollState(final);
            return;
          }
        } else if (res?.status === 401) {
          // C2 has already tried to renew the session; a signed-in user whose renewal was
          // refused is redirected by it. Anyone still here needs to sign in first.
          setPollState("sign-in");
          return;
        } else if (res?.status === 404 || res?.status === 403) {
          setPollState("not-found");
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (!cancelled) {
        setPollState("timeout");
      }
    }

    void pollEnrollment(paymentId);

    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  const dashboardHref = `/t/${tenantSlug}/dashboard`;
  const location = useLocation();
  const signInHref = `/t/${tenantSlug}/login?next=${encodeURIComponent(
    `${location.pathname}${location.search}`,
  )}`;

  return (
    <>
      {pollState === "confirming" && (
        <p className="mt-4 text-[var(--text-secondary)]" data-testid="checkout-success-pending">
          {t("confirmingEnrollment")}
        </p>
      )}

      {pollState === "ready" && (
        <p className="mt-4 text-[var(--text-secondary)]" data-testid="checkout-success-enrolled">
          {t("enrollmentActive")}
        </p>
      )}

      {pollState === "timeout" && (
        <div className="mt-4 space-y-2" data-testid="checkout-success-timeout">
          <p className="text-[var(--text-secondary)]">{t("enrollmentProcessing")}</p>
        </div>
      )}

      {pollState === "sign-in" && (
        <div className="mt-4 space-y-2" data-testid="checkout-success-sign-in">
          <p className="text-[var(--text-secondary)]">{t("signInToConfirm")}</p>
          <Link
            to={signInHref}
            className="inline-block text-sm text-[var(--text-heading)] underline"
            data-testid="checkout-success-sign-in-link"
          >
            {t("signIn")}
          </Link>
        </div>
      )}

      {pollState === "not-found" && (
        <p className="mt-4 text-[var(--text-secondary)]" data-testid="checkout-success-not-found">
          {t("paymentNotFound")}
        </p>
      )}

      {pollState === "not-completed" && (
        <p
          className="mt-4 text-[var(--text-secondary)]"
          data-testid="checkout-success-not-completed"
        >
          {t("paymentNotCompleted")}
        </p>
      )}

      {pollState === "duplicate" && (
        <p className="mt-4 text-[var(--text-secondary)]" data-testid="checkout-success-duplicate">
          {t("paymentDuplicate")}
        </p>
      )}

      {pollState === "refunded" && (
        <p className="mt-4 text-[var(--text-secondary)]" data-testid="checkout-success-refunded">
          {t("paymentRefunded")}
        </p>
      )}

      {pollState === "no-payment" && (
        <p className="mt-4 text-[var(--text-secondary)]" data-testid="checkout-success-generic">
          {t("enrollmentGeneric")}
        </p>
      )}

      <Link
        to={dashboardHref}
        className="mt-8 inline-block rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)]"
        data-testid="checkout-success-dashboard"
      >
        {t("goToDashboard")}
      </Link>
    </>
  );
}

export function TenantCheckoutSuccessPage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const t = useTranslations("checkout");
  const paymentId = searchParams.get("paymentId") ?? undefined;
  const subscriptionPaymentId = searchParams.get("subscriptionPaymentId") ?? undefined;

  return (
    <main className="mx-auto max-w-lg px-4 py-16" data-testid="checkout-success">
      <h1 className="text-2xl font-semibold text-green-800 dark:text-green-400">
        {t("paymentSuccess")}
      </h1>
      {(paymentId ?? subscriptionPaymentId) && (
        <p
          className="mt-2 text-xs text-[var(--text-muted)]"
          data-testid="checkout-success-payment-id"
        >
          {t("paymentId", { paymentId: paymentId ?? subscriptionPaymentId ?? "" })}
        </p>
      )}
      <CheckoutSuccessClient
        tenantSlug={tenantSlug}
        paymentId={paymentId}
        subscriptionPaymentId={subscriptionPaymentId}
      />
    </main>
  );
}

export function TenantMockCheckoutPage() {
  const { tenantSlug = "", paymentId = "" } = useParams<{
    tenantSlug: string;
    paymentId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const t = useTranslations("checkout");
  const tRoot = useTranslations();
  const provider = searchParams.get("provider") ?? "razorpay";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function completePayment() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/v1/checkout/e2e-complete", {
        method: "POST",
        body: JSON.stringify({ paymentId }),
      });
      if (!res.ok) {
        setError(
          apiErrorText(await readApiErrorResponse(res), tRoot, "checkout.mockFailed", {
            namespace: "checkout.errors",
          }),
        );
        return;
      }
      void navigate(`/t/${tenantSlug}/checkout/success?paymentId=${paymentId}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto max-w-lg px-4 py-16" data-testid="checkout-mock">
      <h1 className="text-2xl font-semibold">{t("mockTitle")}</h1>
      <p className="mt-2 text-sm text-[var(--text-muted)]">
        {t("mockProvider")} <span data-testid="checkout-mock-provider">{provider}</span>
      </p>
      <button
        type="button"
        onClick={() => void completePayment()}
        disabled={loading}
        className="mt-8 rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
        data-testid="checkout-mock-complete"
      >
        {loading ? t("mockProcessing") : t("mockComplete")}
      </button>
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600" data-testid="checkout-mock-error">
          {error}
        </p>
      )}
      <Link
        to={`/t/${tenantSlug}/courses`}
        className="mt-6 inline-block text-sm text-[var(--text-heading)] underline"
      >
        {t("mockCancel")}
      </Link>
    </main>
  );
}

export function TenantMockSubscriptionCheckoutPage() {
  const { tenantSlug = "", subscriptionPaymentId = "" } = useParams<{
    tenantSlug: string;
    subscriptionPaymentId: string;
  }>();
  const [searchParams] = useSearchParams();
  const tErrors = useTranslations("errors");
  const e2eMode = import.meta.env.VITE_E2E_TEST_MODE === "true";
  const provider = searchParams.get("provider") ?? "razorpay";

  if (!e2eMode) {
    return (
      <main className="mx-auto max-w-lg px-4 py-16">
        <p className="text-sm text-[var(--text-muted)]">{tErrors("NOT_FOUND")}</p>
      </main>
    );
  }

  return (
    <MockSubscriptionCheckoutPanel
      tenantSlug={tenantSlug}
      subscriptionPaymentId={subscriptionPaymentId}
      provider={provider}
    />
  );
}
